import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { CalculatorProps, FormState, SqueezedMode } from '../types';
import { CalculatorInput } from './CalculatorInput';
import { number, object } from 'yup';
import {
  defaultAlignment,
  defaultAspectRatio,
  defaultLensSqueezeFactor,
} from 'src/domains/api/static';
import { KEY } from '../constants';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { createEncodedObjectParam } from 'src/domains/shared/hooks/useQueryParams/EncodedObjectParam';
import { DropdownOption } from '@hawkins/components';
import { CameraSensorMode } from 'src/domains/api/cameras/types';
import { useEffectOnce } from 'react-use';
import { calculateFramingAndWorkingResolution } from './FramingChart/calculations';
import { CalculatorOutputProps } from './CalculatorOutput/types';
import { Alignment } from '../../utilities/types';
import { getFramingCalculatorDownloadValidation } from './utilities/validation';

const validationSchema = object({
  alignment: object().default(defaultAlignment),
  sensorMode: object().required('Please select a camera and sensor mode.'),
  outputAspectRatio: object().default(defaultAspectRatio),
  lensSqueezeFactor: object().default(defaultLensSqueezeFactor),
  safetyCrop: number().min(0).max(100).nullable().default(0),
  customCameraAspectRatio: number().optional(),
  effectiveCanvasWidth: number().optional(),
  effectiveCanvasHeight: number().optional(),
  customFramingDecisionWidth: number().optional(),
  customFramingDecisionHeight: number().optional(),
}).required();

const defaultFormValues: FormState = {
  alignment: defaultAlignment,
  sensorMode: null,
  outputAspectRatio: defaultAspectRatio,
  lensSqueezeFactor: defaultLensSqueezeFactor,
  safetyCrop: 0,
  customCameraAspectRatio: null,
  effectiveCanvasWidth: null,
  effectiveCanvasHeight: null,
  customFramingDecisionWidth: null,
  customFramingDecisionHeight: null,
  squeezedMode: SqueezedMode.Squeezed,
  framingIntentLabel: '',
  showTitle: '',
  directorOfPhotography: '',
  lockAspectRatio: false,
};

/**
 * Main Calculator component that orchestrates input collection and calculations.
 * This component owns all form state and calculations, passing them down to CalculatorInput for display.
 */
export const Calculator = (props: CalculatorProps) => {
  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      import('@netflix/nfa-logger/lib/helper').then(({ nfa }) => {
        nfa.logFeatureEngaged('calculators', KEY);
      });
    }
  });

  const [queryParams, setQueryParams] = useQueryParams({
    alignment: withDefault(
      createEncodedObjectParam<string, DropdownOption<string>>(),
      defaultAlignment
    ),
    sensorMode: createEncodedObjectParam<
      string,
      DropdownOption<CameraSensorMode>
    >(),
    outputAspectRatio: withDefault(
      createEncodedObjectParam<string, DropdownOption<number>>(),
      defaultAspectRatio
    ),
    lensSqueezeFactor: withDefault(
      createEncodedObjectParam<string, DropdownOption<number>>(),
      defaultLensSqueezeFactor
    ),
    safetyCrop: withDefault(NumberParam, 0),
    customCameraAspectRatio: NumberParam,
    effectiveCanvasWidth: NumberParam,
    effectiveCanvasHeight: NumberParam,
    customFramingDecisionWidth: NumberParam,
    customFramingDecisionHeight: NumberParam,
    squeezedMode: withDefault(NumberParam, SqueezedMode.Squeezed),
    framingIntentLabel: withDefault(
      StringParam,
      defaultFormValues.framingIntentLabel
    ),
    showTitle: withDefault(StringParam, defaultFormValues.showTitle),
    directorOfPhotography: withDefault(
      StringParam,
      defaultFormValues.directorOfPhotography
    ),
    lockAspectRatio: withDefault(BooleanParam, false),
  });

  const form = useFormik({
    initialValues: queryParams,
    validationSchema,
    onReset: (values) => {
      setQueryParams(values);
      props.onReset(KEY);
    },
    onSubmit: async (_values, actions) => {
      actions.setSubmitting(false);
    },
  });

  // Perform all calculations , memoized to prevent unnecessary re-renders
  const calculationResults = useMemo(() => {
    if (form.values.sensorMode?.value) {
      return calculateFramingAndWorkingResolution({
        formValues: form.values,
      });
    }
    return null;
  }, [form.values]);

  // Calculate framing chart again with desqueezed values for display, if applicable
  // All other calculations in the app assume the selected lens squeeze factor will be applied
  const desqueezedCalculationResults = useMemo(() => {
    if (form.values.lensSqueezeFactor?.value !== 1) {
      return calculateFramingAndWorkingResolution({
        formValues: form.values,
        desqueezed: true,
      });
    }
  }, [form.values]);

  // Compose final output from calculation results + form state
  const finalOutput = useMemo(() => {
    if (!calculationResults) return null;

    const downloadValidation = getFramingCalculatorDownloadValidation(
      form.values,
      calculationResults.framingChartProps
    );

    const output: CalculatorOutputProps = {
      // Core calculated values
      ...calculationResults,

      // Form values needed for display
      alignment: form.values.alignment.value as Alignment,
      downloadValidation,

      // Optional computed props for desqueezed framing chart
      desqueezedFramingChartProps:
        desqueezedCalculationResults?.framingChartProps,

      squeezedMode: form.values.squeezedMode,
    };

    return output;
  }, [calculationResults, desqueezedCalculationResults, form.values]);

  useEffect(() => {
    if (!finalOutput) {
      return;
    }

    /** Invoke onChange callback for parent to hold state */
    props.onChange(KEY, form.values, finalOutput);

    /** Set query parameters for URL sharing */
    setQueryParams(form.values);
    // Disable exhaustive deps warning here due to infinite loop issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values, props.onChange, setQueryParams, finalOutput]);

  return (
    <CalculatorInput
      calculatedInputValues={
        finalOutput
          ? {
              aspectRatio: finalOutput.framingChartProps.aspectRatio[0],
              protection: finalOutput.framingChartProps.protection * 100,
            }
          : null
      }
      defaultFormValues={defaultFormValues}
      form={form}
      canvasResolution={
        calculationResults?.framingChartProps.canvasResolution ?? null
      }
      effectiveCanvas={
        calculationResults?.framingChartProps.effectiveCanvas ?? null
      }
    />
  );
};
